import { makeStyles } from '@mui/styles';
import Accordion from 'apollo-react/components/Accordion';
import AccordionDetails from 'apollo-react/components/AccordionDetails';
import AccordionSummary from 'apollo-react/components/AccordionSummary';
import Grid from 'apollo-react/components/Grid';
import Typography from 'apollo-react/components/Typography';
import classNames from 'classnames';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';

import DetailsRow from './DetailsRow';
import RightsBlock from './RightsBlock';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import {
  ENTITY_TYPE_CHILD,
  ENTITY_TYPE_PARENT,
  RUNTIME_TABLE_VALUES,
} from '../../constants/entity-designer.constants';
import { EntityContext } from '../../context';
import {
  getEntityProp,
  getEntityType,
  getIsLabelDisabled,
  getIsRuntimeTableDropdownDisabled,
} from '../../selectors/entity-designer.selectors';
import BooleanControl from '../editor/controls/Boolean';
import CheckboxControl from '../editor/controls/Checkbox';
import SelectControl from '../editor/controls/Select';
import TextBoxControl from '../editor/controls/TextBox';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 10,
    width: '90%',
  },
  column: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 600,
    marginTop: 5,
    marginRight: 35,
  },
  description: {
    marginTop: 5,
  },
  firstColumn: {
    marginTop: 4,
  },
});

const Details = () => {
  const classes = useStyles();
  const translateString = useTranslation();
  const isEditMode = useContext(EntityContext);

  const entityType = useSelector(getEntityType);
  const isLabelDisabled = useSelector(getIsLabelDisabled);
  const isParentEntity = useSelector((state) => getEntityProp(state, { prop: 'IsParent' }));
  const isGenericTable = useSelector((state) => getEntityProp(state, { prop: 'IsGenericTable' }));
  const isAllowRestrictedAccessVisible = useSelector((state) =>
    getEntityProp(state, { prop: 'IsAllowRestrictedAccessVisible' })
  );
  const isAllowRestrictedAccessEnabled = useSelector((state) =>
    getEntityProp(state, { prop: 'IsAllowRestrictedAccessEnabled' })
  );

  const isRuntimeTableDropdownDisabled = useSelector(getIsRuntimeTableDropdownDisabled);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary>
        <Typography>{translateString('Entity Details')}</Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.root}>
        <Grid container>
          <>
            <Grid item className={classes.firstColumn} xs={12} lg={5}>
              <div className={classes.column}>
                <TextBoxControl label="Name" prop="EntityName" disabled={!isEditMode} />
              </div>
            </Grid>
            <Grid item xs={12} lg={5}>
              <div className={classes.column}>
                {isParentEntity && (
                  <TextBoxControl
                    label="Label"
                    prop="Label"
                    disabled={!isEditMode || isLabelDisabled}
                  />
                )}
              </div>
            </Grid>
          </>
          <DetailsRow isEditMode={isEditMode} leftLabel="Description" leftFieldKey="Description" />
          {isParentEntity && <RightsBlock />}
          <>
            <Grid item className={classes.firstColumn} xs={12} lg={5}>
              <div className={classes.column}>
                <TextBoxControl label="View Name" prop="EntityViewName" disabled={!isEditMode} />
              </div>
            </Grid>
            <Grid item xs={12} lg={5}>
              <div className={classes.column}>
                {!isGenericTable ? (
                  <TextBoxControl label="Runtime Table" prop="RuntimeTable" disabled />
                ) : (
                  <SelectControl
                    label="Runtime Table"
                    prop="RuntimeTable"
                    items={RUNTIME_TABLE_VALUES[entityType]}
                    disabled={!isEditMode || isRuntimeTableDropdownDisabled}
                  />
                )}
              </div>
            </Grid>
          </>
          <>
            <Grid item xs={12} lg={5}>
              <div className={classNames(classes.column, classes.firstColumn)}>
                <BooleanControl label="Is Parent Entity?" prop="IsParent" />
              </div>
            </Grid>
            <Grid item xs={12} lg={5}>
              <div className={classes.column}>
                {[ENTITY_TYPE_PARENT, ENTITY_TYPE_CHILD].includes(entityType) && (
                  <TextBoxControl
                    label="Object Table Name"
                    prop="ObjectTable"
                    disabled={!isEditMode}
                  />
                )}
              </div>
            </Grid>
          </>
          <>
            <Grid item xs={12} lg={5}>
              <div className={classNames(classes.column, classes.firstColumn)}>
                <BooleanControl label="Is Master Setup Entity?" prop="IsMasterSetup" />
              </div>
            </Grid>
            {isAllowRestrictedAccessVisible && (
              <Grid item xs={12} lg={5}>
                <div className={classes.column}>
                  <CheckboxControl
                    label="Allow Restricted Access?"
                    prop="AllowResAccess"
                    disabled={!isEditMode || !isAllowRestrictedAccessEnabled}
                  />
                </div>
              </Grid>
            )}
          </>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default Details;
